import { Modal, Text, Box, Flex, Divider, Select, Switch, Button } from '@mantine/core';
import { IconFile } from '@tabler/icons-react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { isNotEmpty, useForm } from '@mantine/form';
import { DateInput } from 'src/common/components/date/DateInput';
import { FundReportType } from 'src/company/types';

export type FundDocumentUploadData = {
  reportType: FundReportType | null;
  reportDate: Date | null;
  isLatest: boolean;
};

type FundDocumentUploadModalProps = {
  opened: boolean;
  onClose: () => void;
  file: File;
  onSubmit: (values: FundDocumentUploadData) => void;
};

export const ReportTypeToLabel = {
  FUND_FACT_SHEET: 'Fund fact sheet',
  RISK_PROFILE: 'Risk profile',
  OTHER: 'Other',
};

export default function FundDocumentUploadModal({
  opened,
  onClose,
  file,
  onSubmit,
}: FundDocumentUploadModalProps) {
  const form = useForm<FundDocumentUploadData>({
    mode: 'uncontrolled',
    initialValues: {
      reportType: null,
      reportDate: null,
      isLatest: true,
    },
    validate: {
      reportType: isNotEmpty('Report type is required'),
      reportDate: isNotEmpty('Report date is required'),
    },
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Text size="displayXs" fw={FONT_WEIGHTS.semibold} c="#1a1a1a">
          Upload document
        </Text>
      }
    >
      <form role="form" onSubmit={form.onSubmit(onSubmit)}>
        <Box>
          <Flex direction={'row'} align={'center'}>
            <IconFile />
            <Text
              size="xs"
              ml={'xxxs'}
              fw={FONT_WEIGHTS.semibold}
              c="gray.8"
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100%',
              }}
            >
              {file.name}
            </Text>
          </Flex>
          <Divider my="md" />
          <Select
            label="Report type"
            labelProps={{ variant: 'light' }}
            placeholder="Select report type"
            data={[
              { value: 'FUND_FACT_SHEET', label: ReportTypeToLabel.FUND_FACT_SHEET },
              { value: 'RISK_PROFILE', label: ReportTypeToLabel.RISK_PROFILE },
              { value: 'OTHER', label: ReportTypeToLabel.OTHER },
            ]}
            {...form.getInputProps('reportType')}
          />

          <DateInput
            labelProps={{ variant: 'light' }}
            mt="xs"
            label={'Report date'}
            {...form.getInputProps('reportDate')}
          />
          <Text size={'sm'} c={'gray.7'}>
            Enter the date this document was issued or published
          </Text>
          <Divider my="md" />

          <Flex direction={'row'}>
            <Switch {...form.getInputProps('isLatest', { type: 'checkbox' })} />
            <Text ml={'xs'} size={'sm'} fw={FONT_WEIGHTS.bold}>
              Mark as latest document?
            </Text>
          </Flex>
          <Text mt={'sm'} size={'sm'} c={'gray.7'}>
            Siesta AI shows latest documents to include with generated reports with option to select
            other documents.
          </Text>

          <Button mt={'md'} fullWidth disabled={!form.isValid()} type={'submit'}>
            Continue
          </Button>
        </Box>
      </form>
    </Modal>
  );
}
